// Imports
import { faCalendar, faCreditCard, faUser, faPen, faPlus } from '@fortawesome/pro-regular-svg-icons';
import React, { useContext, useEffect, useState } from 'react';
import Box from '../../../shareables/foundation/admin-ui/components/content/box';
import DetailsBox from '../../../shareables/foundation/admin-ui/components/content/details-box';
import Heading from '../../../shareables/foundation/admin-ui/components/content/heading';
import PageAction from '../../../shareables/foundation/admin-ui/components/content/page-action';
import PageActions from '../../../shareables/foundation/admin-ui/components/content/page-actions';
import Table from '../../../shareables/foundation/admin-ui/components/content/table';
import { useAppDispatch } from '../../../shareables/foundation/front-end/redux/hooks';
import apiActions from '../../../shareables/integrations/api/redux/actions';
import { GetBillingResponse } from '../../../shareables/types/api/main/reseller/billing/get';
import ResellerContext from '../../resources/reseller-provider/context';
import TransactionRow from './row';


// Define the accepted props
interface Props {
	storybookBilling?: GetBillingResponse;
}


// Function component
const Billing: React.FC<Props> = ({ storybookBilling }) => {
	// Use state
	const [billing, setBilling] = useState<GetBillingResponse | null>(storybookBilling ?? null);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Use context
	const reseller = useContext(ResellerContext);
	
	
	// Load billing on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetBillingResponse>({
				action: 'GET',
				uri: `/reseller/${reseller?.id ?? ''}/billing`,
				completion: (json) => {
					setBilling(json.response);
				},
			})
		);
	}, [reseller?.id, dispatch]);
	
	
	// If loading, then display nothing
	if (billing === null) {
		return null;
	}
	
	
	// Return JSX
	return (
		<React.Fragment>
			<Heading size={1}>Billing</Heading>
			
			<PageActions>
				<PageAction
					to={`${billing.paymentMethod ? 'replace' : 'add'}-payment-method`}
					icon={billing.paymentMethod ? faPen : faPlus}
					text={`${billing.paymentMethod ? 'Replace' : 'Add'} payment method`}
					tooltip={billing.paymentMethod ? 'Update your stored credit/debit card' : 'Store a new credit/debit card'}
				/>
			</PageActions>
			
			{billing.paymentMethod ? (
				<DetailsBox
					details={[
						{
							icon: faCreditCard,
							text: `${billing.paymentMethod.brand} ${billing.paymentMethod.last4}`,
							tooltip: 'Descriptor',
						},
						{
							icon: faUser,
							text: billing.paymentMethod.name,
							tooltip: 'Cardholder',
						},
						{
							icon: faCalendar,
							text: `${billing.paymentMethod.expMonth}/${billing.paymentMethod.expYear}`,
							tooltip: 'Expiry',
						},
					]}
				/>
			) : (
				<Box>
					<em>Add a payment method using the button above to pay for resold websites.</em>
				</Box>
			)}
			
			{Boolean(billing.transactions.length) && (
				<Box>
					<Heading size={2} centered>
						Transaction history
					</Heading>
					
					<Table>
						<thead>
							<tr>
								<th>Date</th>
								
								<th>Details</th>
								
								<th>Method</th>
								
								<th style={{ textAlign: 'center' }}>Receipt</th>
							</tr>
						</thead>
						
						<tbody>
							{billing.transactions.map((transaction) => (
								<TransactionRow key={transaction.id} transaction={transaction} />
							))}
						</tbody>
					</Table>
				</Box>
			)}
		</React.Fragment>
	);
};

export default Billing;
